import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { claimsApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Confetti from "./Confetti";
import Countdown from "./Countdown";
import { toast } from "sonner";
import { Copy, Check, MapPin } from "lucide-react";

// ASCII-style QR-ish render using the code as input. Good enough as a visual proxy
// (a real QR would require a heavier lib; the code beneath is what gets scanned).
function FauxQR({ code }) {
  // hash code → boolean grid
  const seed = (s) => {
    let h = 5381;
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h) + s.charCodeAt(i);
    return Math.abs(h);
  };
  const grid = [];
  let h = seed(code || "X");
  for (let r = 0; r < 21; r++) {
    const row = [];
    for (let c = 0; c < 21; c++) {
      h = (h * 1103515245 + 12345) & 0x7fffffff;
      row.push(h % 2 === 0);
    }
    grid.push(row);
  }
  // finder squares (top-left, top-right, bottom-left)
  const isFinder = (r, c) =>
    (r < 7 && c < 7) || (r < 7 && c > 13) || (r > 13 && c < 7);
  return (
    <div className="bg-[#FAF6E8] p-4 rounded-2xl border-2 border-[#0B0F0C]">
      <div className="grid" style={{ gridTemplateColumns: "repeat(21, 1fr)", gap: 2 }}>
        {grid.map((row, r) =>
          row.map((v, c) => {
            const finder = isFinder(r, c);
            const inFinderRing =
              ((r === 0 || r === 6) && c < 7) ||
              ((c === 0 || c === 6) && r < 7) ||
              ((r === 0 || r === 6) && c > 13) ||
              ((c === 14 || c === 20) && r < 7) ||
              ((r === 14 || r === 20) && c < 7) ||
              ((c === 0 || c === 6) && r > 13);
            const inFinderCore = (r >= 2 && r <= 4 && c >= 2 && c <= 4) ||
                                 (r >= 2 && r <= 4 && c >= 16 && c <= 18) ||
                                 (r >= 16 && r <= 18 && c >= 2 && c <= 4);
            const dark = finder ? (inFinderRing || inFinderCore) : v;
            return (
              <div
                key={`${r}-${c}`}
                style={{ aspectRatio: "1/1", background: dark ? "#0B0F0C" : "transparent" }}
              />
            );
          })
        )}
      </div>
    </div>
  );
}

export default function QRClaimModal({ deal, onClose }) {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confetti, setConfetti] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      onClose?.();
      nav("/auth?mode=login");
      return;
    }
    let geo = {};
    const finish = async (g) => {
      try {
        const c = await claimsApi.claim(deal.id, g);
        setClaim(c);
        setConfetti(true);
        setTimeout(() => setConfetti(false), 2000);
        // refresh user xp
        refresh();
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not claim");
      } finally { setLoading(false); }
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => finish({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => finish({}),
        { timeout: 3000 },
      );
    } else { finish({}); }
    // eslint-disable-next-line
  }, []);

  const copyCode = () => {
    if (!claim?.code) return;
    navigator.clipboard.writeText(claim.code);
    setCopied(true);
    toast.success("Code copied");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        data-testid="qr-claim-modal"
        className="max-w-md rounded-3xl border-2 border-[#0B0F0C] bg-[#FAF6E8] [&>button]:hidden p-0 overflow-hidden shadow-[10px_10px_0_#15FF73] relative"
      >
        {confetti && <Confetti />}
        <div className="bg-[#15FF73] border-b-2 border-[#0B0F0C] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-mono-x text-[10px] font-bold uppercase tracking-widest">Deal claimed!</div>
            <div className="font-display text-xl font-bold leading-tight">{deal.title}</div>
          </div>
          <button onClick={onClose} className="text-[#0B0F0C] text-xl font-bold">×</button>
        </div>
        <div className="p-6">
          {loading && (
            <div className="text-center py-8">
              <div className="font-display text-2xl">Locking it in…</div>
              <div className="text-sm text-[#0B0F0C]/60 mt-2">+10 XP incoming 🌿</div>
            </div>
          )}
          {error && !loading && (
            <div className="text-center py-6">
              <div className="text-3xl mb-2">😬</div>
              <div className="font-display text-xl font-bold">{error}</div>
              <button onClick={onClose} className="btn-x btn-ghost mt-5">Close</button>
            </div>
          )}
          {claim && !loading && (
            <>
              <div className="text-center mb-5">
                <div className="text-xs font-mono-x font-bold uppercase tracking-widest text-[#0B0F0C]/60 mb-2">Show this at the counter</div>
                <FauxQR code={claim.code} />
              </div>

              <div className="flex items-center gap-2 mb-4 bg-[#0B0F0C] text-[#15FF73] px-4 py-3 rounded-2xl">
                <span className="font-mono-x text-2xl font-bold flex-1 tracking-widest text-center">{claim.code}</span>
                <button
                  data-testid="copy-claim-code"
                  onClick={copyCode}
                  className="text-[#15FF73] hover:text-white"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="rounded-2xl border-2 border-[#0B0F0C] p-3">
                  <div className="label-overline text-[#0B0F0C]/50">Save</div>
                  <div className="font-display text-2xl font-extrabold">${(deal.original_price - deal.deal_price).toFixed(0)}</div>
                </div>
                <div className="rounded-2xl border-2 border-[#0B0F0C] p-3 bg-[#FFE74C]">
                  <div className="label-overline text-[#0B0F0C]/60">Expires</div>
                  <Countdown to={claim.expires_at} className="text-lg font-bold" />
                </div>
              </div>

              {claim.xp_award && (
                <div className="rounded-2xl border-2 border-[#0B0F0C] bg-[#FF1A8C] text-white p-4 mb-5 flex items-center gap-3">
                  <span className="text-2xl">{claim.xp_award.level?.icon || "✨"}</span>
                  <div>
                    <div className="font-display text-xl font-bold leading-none">+{claim.xp_award.delta} XP</div>
                    <div className="text-xs opacity-90">{claim.xp_award.level?.name} · {claim.xp_award.streak}-day streak 🔥</div>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-[#0B0F0C]/60 mb-4">
                <MapPin size={12} /> Walk in within 24h. Staff scans → done.
              </div>

              <button onClick={() => { onClose?.(); nav("/me"); }} className="btn-x btn-primary w-full">
                See my claims →
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
